import { createContext, useContext, useEffect, useState } from "react";
import { AdminRole, ReactChildrenProps } from "global";
import {
  SignInMutationVariables,
  useGetMyAdminUserLazyQuery,
  useSignInMutation,
  useSignOutMutation,
} from "../../../lib/apollo/graphql/generated";
import Loader from "../components/Loader";

export type AdminUser = {
  id: string;
  role: AdminRole;
} | null;

type AdminContext = {
  user: AdminUser;
  login: ({ variables }: { variables: SignInMutationVariables }) => void;
  logout: () => void;
  isLoading: boolean;
};

const AdminUserContext = createContext<AdminContext>({
  user: null,
  isLoading: false,
  logout: () => {},
  login: ({ variables }: { variables: SignInMutationVariables }) => {},
});

export default function AdminUserProvider({ children }: ReactChildrenProps) {
  const [user, setUser] = useState<AdminUser>(null);

  const [login, { loading: isLoggingIn }] = useSignInMutation({
    onCompleted: (data) => {
      const user = data?.signInAsAdmin;

      setUser({
        id: user?.email,
        role: user?.adminRole?.name as AdminRole,
      });
    },
    onError: () => {
      window.alert("로그인 정보가 틀렸습니다.");
      setUser(null);
    },
  });

  const [logout, { loading: isLoggingOut }] = useSignOutMutation({
    onCompleted: () => {
      setUser(null);
      localStorage.removeItem("authToken");
      window.location.href = "/";
    },
  });

  const [getUser, { loading }] = useGetMyAdminUserLazyQuery({
    onCompleted: (res) => {
      const user = res.getMyAdminUser;

      if (user) {
        setUser({
          id: user.email,
          role: user.adminRole?.name as AdminRole,
        });
      } else {
        setUser(null);
      }
    },
    onError: () => {
      setUser(null);
    },
  });

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    if (token) {
      getUser();
    } else {
      setUser(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loader />;
  }

  return (
    <AdminUserContext.Provider
      value={{
        user,
        login,
        logout,
        isLoading: isLoggingIn || isLoggingOut,
      }}
    >
      {children}
    </AdminUserContext.Provider>
  );
}

export function useAdminUserContext() {
  return useContext(AdminUserContext);
}
